import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { LoadingSpinner, ErrorBanner, EmptyState } from '../components/AdminStatus';

const API   = 'https://api.udanchu.com';
const font  = "'DM Sans', system-ui, sans-serif";
const LIMIT = 20;

const getKey = () => sessionStorage.getItem('udanchu_admin_key') || '';

const DOMAIN_COLORS = {
  traffic:  { bg: 'rgba(79,142,247,0.15)',  color: '#4f8ef7' },
  consumer: { bg: 'rgba(52,211,153,0.15)',  color: '#34d399' },
  police:   { bg: 'rgba(251,191,36,0.15)',  color: '#fbbf24' },
};
const domainStyle = (domain) =>
  DOMAIN_COLORS[domain] || { bg: 'rgba(100,116,139,0.15)', color: '#64748b' };

const s = {
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '20px',
  },
  tab: {
    background: 'transparent',
    border: '1px solid #252a38',
    borderRadius: '6px',
    color: '#64748b',
    fontFamily: font,
    fontSize: '13px',
    fontWeight: '500',
    padding: '6px 14px',
    cursor: 'pointer',
  },
  tabActive: {
    background: '#4f8ef7',
    border: '1px solid #4f8ef7',
    color: '#fff',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  row: {
    background: '#181c27',
    border: '1px solid #252a38',
    borderRadius: '10px',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
  },
  rowTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '4px',
  },
  badge: {
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.06em',
    padding: '3px 7px',
    fontFamily: font,
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  title: {
    color: '#e2e8f0',
    fontFamily: font,
    fontSize: '14px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  snippet: {
    color: '#64748b',
    fontFamily: font,
    fontSize: '13px',
    lineHeight: '1.5',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
    marginTop: '2px',
  },
  viewBtn: {
    background: 'transparent',
    border: '1px solid #252a38',
    borderRadius: '6px',
    color: '#64748b',
    fontFamily: font,
    fontSize: '12px',
    fontWeight: '500',
    padding: '5px 11px',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
  },
  approveBtn: {
    background: 'rgba(52,211,153,0.12)',
    border: '1px solid rgba(52,211,153,0.35)',
    borderRadius: '6px',
    color: '#34d399',
    fontFamily: font,
    fontSize: '12px',
    fontWeight: '600',
    padding: '5px 11px',
    cursor: 'pointer',
  },
  rejectBtn: {
    background: 'rgba(248,113,113,0.12)',
    border: '1px solid rgba(248,113,113,0.35)',
    borderRadius: '6px',
    color: '#f87171',
    fontFamily: font,
    fontSize: '12px',
    fontWeight: '600',
    padding: '5px 11px',
    cursor: 'pointer',
  },
  btnDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginTop: '24px',
    fontFamily: font,
  },
  pageBtn: {
    background: 'transparent',
    border: '1px solid #252a38',
    borderRadius: '6px',
    color: '#64748b',
    fontFamily: font,
    fontSize: '13px',
    fontWeight: '500',
    padding: '6px 14px',
    cursor: 'pointer',
  },
  pageInfo: {
    color: '#64748b',
    fontSize: '13px',
  },
  actionError: {
    marginTop: '12px',
  },
};

function CardRow({ card, onApprove, onReject, busy }) {
  const ds = domainStyle(card.domain);
  return (
    <div style={s.row}>
      <div style={s.rowBody}>
        <div style={s.rowTop}>
          <span style={{ ...s.badge, background: ds.bg, color: ds.color }}>
            {card.domain || 'general'}
          </span>
          <span style={s.title}>{card.title}</span>
        </div>
        {card.short_answer && (
          <div style={s.snippet}>{card.short_answer}</div>
        )}
      </div>
      <div style={s.actions}>
        {busy ? (
          <span style={{ color: '#64748b', fontFamily: font, fontSize: '12px' }}>…</span>
        ) : (
          <>
            <Link to={`/admin/card/${card.id}`} style={s.viewBtn}>View</Link>
            <button style={s.approveBtn} onClick={() => onApprove(card.id)}>✓ Approve</button>
            <button style={s.rejectBtn}  onClick={() => onReject(card.id)}>✗ Reject</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminReview() {
  const [drafts,        setDrafts]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [filter,        setFilter]        = useState('pending');
  const [page,          setPage]          = useState(1);
  const [total,         setTotal]         = useState(0);
  const [actionBusy,    setActionBusy]    = useState(null);
  const [actionError,   setActionError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDrafts([]);

    const STATUS_MAP = { pending: 'draft', approved: 'active', rejected: 'deprecated' };
    const status = STATUS_MAP[filter] || '';
    const params = new URLSearchParams({ page, limit: LIMIT });
    if (status) params.set('status', status);

    fetch(`${API}/cards?${params}`, { headers: { 'X-Admin-Key': getKey() } })
      .then(r => r.ok ? r.json() : r.json().then(j => Promise.reject(j.message || `Error ${r.status}`)))
      .then(data => { if (!cancelled) { setDrafts(data.cards || []); setTotal(data.total || 0); } })
      .catch(err  => { if (!cancelled) setError(typeof err === 'string' ? err : 'Failed to load cards.'); })
      .finally(()  => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [filter, page]);

  const patchCard = async (id, status, label) => {
    setActionBusy(id);
    setActionError(null);
    try {
      const res = await fetch(`${API}/cards/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': getKey() },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        setDrafts(prev => prev.filter(c => c.id !== id));
        setTotal(prev => Math.max(0, prev - 1));
      } else {
        const j = await res.json().catch(() => ({}));
        setActionError(j.message || `${label} failed (${res.status})`);
      }
    } catch {
      setActionError(`Could not reach the API — ${label.toLowerCase()} not saved.`);
    } finally {
      setActionBusy(null);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  const tabs = [
    { key: 'pending',  label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'all',      label: 'All' },
  ];

  return (
    <AdminLayout title="Draft Review Queue">
      <div style={s.toolbar}>
        {tabs.map(t => (
          <button
            key={t.key}
            style={{ ...s.tab, ...(filter === t.key ? s.tabActive : {}) }}
            onClick={() => { setFilter(t.key); setPage(1); }}
          >
            {t.label}
          </button>
        ))}
        {!loading && !error && (
          <span style={{ color: '#64748b', fontFamily: font, fontSize: '13px', marginLeft: '4px' }}>
            {total} card{total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {loading && <LoadingSpinner message="Loading draft cards..." />}

      {!loading && error && (
        <ErrorBanner error={error} onRetry={() => { setError(null); setLoading(true); setDrafts([]); }} />
      )}

      {actionError && (
        <div style={s.actionError}>
          <ErrorBanner error={actionError} onRetry={() => setActionError(null)} />
        </div>
      )}

      {!loading && !error && drafts.length === 0 && (
        <EmptyState message="No draft cards to review." />
      )}

      {!loading && !error && drafts.length > 0 && (
        <>
          <div style={s.list}>
            {drafts.map(card => (
              <CardRow
                key={card.id}
                card={card}
                busy={actionBusy === card.id}
                onApprove={id => patchCard(id, 'active',     'Approve')}
                onReject= {id => patchCard(id, 'deprecated', 'Reject')}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div style={s.pagination}>
              <button
                style={{ ...s.pageBtn, ...(page <= 1 ? s.btnDisabled : {}) }}
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                ← Prev
              </button>
              <span style={s.pageInfo}>Page {page} of {totalPages}</span>
              <button
                style={{ ...s.pageBtn, ...(page >= totalPages ? s.btnDisabled : {}) }}
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}
